import { Row } from '@tanstack/react-table';
import { Attributes, TableColumn } from '../models';
import { getProcessedValue } from './getProcessedValue';

// NOTE: The logic implemented below originates from DASHUI-4742.
const REGEX_PLACEHOLDER = /\{([^\}]+)\}/g;

/**
 * Replaces placeholders in a template string with
 * corresponding attribute values from a table row.
 * If the corresponding attribute value is not available, use an empty string
 *
 * @param templateLiteral - The string containing placeholders to be replaced.
 * @param row - A table row containing the data.
 * @param attributes - Optional object mapping placeholders to table columns.
 * @param processFunc - Optional function to further process the replaced values.
 * @returns The template string with placeholders replaced by data values.
 */
export const getReplacedTemplateLiteralByAttributes = async (
  templateLiteral: string,
  row: Row<TableColumn>,
  attributes?: Attributes,
  processFunc?: Function,
): Promise<string> => {
  const newLiteral = templateLiteral;
  // Create a Map for optimized attribute lookups.
  // This step pre-fetches and processes values from the table row
  // to quickly replace placeholders in the next steps.
  // If the value exists in the table row, the processed value is stored in the map.
  const attributeValues = new Map<string, string>();
  for (let placeholder in attributes) {
    const attributeValue = row.getValue(attributes[placeholder].attribute);
    if (attributeValue) {
      const processedValue = (await getProcessedValue(attributeValue, attributes[placeholder].processorFunc)) as string;
      attributeValues.set(placeholder, processedValue ?? attributeValue);
    }
  }

  // Depending on whether the placeholder exists in the map,
  // replace the placeholder with its corresponding value (processed if processFunc is provided) or an empty string
  return newLiteral.replace(REGEX_PLACEHOLDER, (_, placeholder) =>
    attributeValues.has(placeholder)
      ? processFunc
        ? processFunc(attributeValues.get(placeholder))
        : attributeValues.get(placeholder)
      : '',
  );
};
